import React, { useEffect, useState } from "react";
import { api, fmtINR, todayISO } from "../lib/api";
import { Save, IndianRupee, Store, Truck } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = { chicken: "Chicken", mutton: "Mutton", offals: "Offals" };

export default function DailyRates() {
  const [date, setDate] = useState(todayISO());
  const [products, setProducts] = useState([]);
  const [retail, setRetail] = useState({});
  const [wholesale, setWholesale] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [pRes, rRes, wRes] = await Promise.all([
      api.get("/products?active_only=true"),
      api.get(`/daily-prices?date=${date}&channel=retail`),
      api.get(`/daily-prices?date=${date}&channel=wholesale`),
    ]);
    setProducts(pRes.data);
    const rm = {};
    rRes.data.forEach((p) => (rm[p.product_id] = p.price_per_kg));
    setRetail(rm);
    const wm = {};
    wRes.data.forEach((p) => (wm[p.product_id] = p.price_per_kg));
    setWholesale(wm);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [date]);

  const buildItems = (map, channel) =>
    Object.entries(map)
      .filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      .map(([product_id, price_per_kg]) => ({
        product_id,
        date,
        channel,
        price_per_kg: parseFloat(price_per_kg) || 0,
      }));

  const saveAll = async () => {
    setSaving(true);
    const items = [
      ...buildItems(retail, "retail"),
      ...buildItems(wholesale, "wholesale"),
    ];
    try {
      await api.post("/daily-prices/bulk", items);
      toast.success(`Saved ${items.length} rates for ${date}`);
      load();
    } catch (e) {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  const copyRetailToWholesale = () => {
    setWholesale({ ...retail });
    toast.info("Copied retail rates to wholesale — review and Save");
  };

  const grouped = products.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto page-enter" data-testid="rates-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pricing</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
            Daily Rates
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            Set separate <b>Retail</b> (POS) and <b>Wholesale</b> rates per kg. Wholesale rates are
            defaults — per-customer overrides still apply.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="rates-date-input"
            className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
          <div className="flex gap-2">
            <button
              onClick={copyRetailToWholesale}
              data-testid="rates-copy-btn"
              className="border border-zinc-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100"
            >
              Copy → WS
            </button>
            <button
              onClick={saveAll}
              disabled={saving}
              data-testid="rates-save-btn"
              className="bg-sky-600 text-white px-5 py-2 text-sm uppercase tracking-wider hover:bg-sky-700 disabled:bg-zinc-400 font-medium flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save All
            </button>
          </div>
        </div>
      </div>

      {Object.entries(grouped).map(([cat, prods]) => (
        <div key={cat} className="mb-8" data-testid={`rates-section-${cat}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {CAT_LABEL[cat] || cat}
          </div>
          <div className="border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">
                    <span className="inline-flex items-center gap-1">
                      <Store className="h-3 w-3 text-sky-600" /> Retail ₹/unit
                    </span>
                  </th>
                  <th className="px-4 py-2 text-right">
                    <span className="inline-flex items-center gap-1">
                      <Truck className="h-3 w-3 text-zinc-900" /> Wholesale ₹/unit
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {prods.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-5 py-6 text-sm text-zinc-500 text-center">
                      No products
                    </td>
                  </tr>
                ) : (
                  prods.map((p) => {
                    const u = p.unit === "piece" ? "pc" : "kg";
                    return (
                    <tr key={p.id} className="border-b border-zinc-200 last:border-b-0">
                      <td className="px-4 py-3 text-sm font-medium">
                        {p.name}
                        {p.unit === "piece" && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700">
                            Per Piece
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <span className="text-zinc-500 text-sm">₹</span>
                          <input
                            type="number"
                            step="0.01"
                            value={retail[p.id] ?? ""}
                            onChange={(e) =>
                              setRetail((r) => ({ ...r, [p.id]: e.target.value }))
                            }
                            data-testid={`rate-retail-${p.id}`}
                            placeholder="0"
                            className="w-24 border border-zinc-300 px-3 py-1.5 font-mono-num text-base text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
                          />
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 w-6">/{u}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <span className="text-zinc-500 text-sm">₹</span>
                          <input
                            type="number"
                            step="0.01"
                            value={wholesale[p.id] ?? ""}
                            onChange={(e) =>
                              setWholesale((w) => ({ ...w, [p.id]: e.target.value }))
                            }
                            data-testid={`rate-wholesale-${p.id}`}
                            placeholder="0"
                            className="w-24 border border-zinc-300 px-3 py-1.5 font-mono-num text-base text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
                          />
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 w-6">/{u}</span>
                        </div>
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
